import { alpha } from '@mui/material/styles';
import { makeStyles } from 'tss-react/mui';

/**
 * Shared class hook so the thumbnail button's `:hover` / `:focus-visible`
 * can reveal the maximize overlay without tss-react class interpolation.
 */
export const VISUALIZATION_OVERLAY_CLASS = 'ai-sidekick-visualization-overlay';

/**
 * Shared class hook so the user query stack's `:hover` /
 * `:focus-within` can reveal its action row without tss-react class
 * interpolation.
 */
export const USER_ACTIONS_CLASS = 'ai-sidekick-message-user-actions';

const useStyles = makeStyles()((theme) => {
  const sharedTypography = {
    ...theme.typography.small_body_regular,
    lineHeight: 1.6,
  };

  return {
    row: {
      display: 'flex',
      width: '100%',
    },
    rowUser: {
      justifyContent: 'flex-end',
    },
    rowAssistant: {
      justifyContent: 'flex-start',
    },
    bubble: {
      overflowWrap: 'anywhere',
      wordBreak: 'break-word',
    },
    bubbleUser: {
      // The 80% cap lives on `userStack`: a percentage max-width here
      // would resolve against the shrink-to-fit stack rather than the
      // row, squeezing short messages.
      maxWidth: '100%',
      padding: theme.new.spacing.medium,
      backgroundColor: theme.new.color.brand[200],
      borderRadius: theme.new.shape.borderRadius.medium,
      color: theme.new.color.textPrimary,
      ...sharedTypography,
      textAlign: 'start',
      whiteSpace: 'pre-wrap',
    },
    bubbleAssistant: {
      // Full width, unlike the user bubble, so a response reads as the
      // timeline's main column rather than a reply aligned to one side.
      width: '100%',
      maxWidth: '100%',
      boxSizing: 'border-box',
      padding: theme.new.spacing.medium,
      backgroundColor: theme.new.color.grey[100],
      borderRadius: theme.new.shape.borderRadius.medium,
      color: theme.new.color.textPrimary,
      ...sharedTypography,
    },
    assistantStack: {
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'flex-start',
      // 8px between the stacked parts of an assistant turn — bubble,
      // visualization, citations, actions row — matching the Figma
      // `response` component. Owned here (the parent) rather than on
      // the rows so each stays context-agnostic if reused outside
      // MessageBubble.
      gap: theme.new.spacing.small,
      // Stretches to the row so the full-width parts inside it resolve
      // against the timeline column rather than a shrink-to-fit stack.
      width: '100%',
      maxWidth: '100%',
    },
    userStack: {
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'flex-end',
      maxWidth: '80%',
      // Containing block for the out-of-flow action row below.
      position: 'relative',
      // The reveal trigger is the query hitbox, so it sits on this
      // shrink-to-fit stack rather than on the full-width row, which
      // also spans the empty side of the timeline. The action row is
      // out of flow but still a descendant, so hovering it still
      // matches and it stays revealed while the pointer is on it.
      [`&:hover .${USER_ACTIONS_CLASS}, &:focus-within .${USER_ACTIONS_CLASS}`]:
        {
          opacity: 1,
        },
    },
    userStackEditing: {
      alignItems: 'stretch',
      width: '100%',
      maxWidth: '100%',
    },
    // Query actions are hover-revealed; response actions are always
    // visible, so only `userActions` carries the opacity transition.
    // `pointerEvents` stays at its default: `none` would put the
    // button out of reach of the keyboard, and `:focus-within` is
    // precisely what lets tabbing reveal it.
    //
    // The row is taken out of flow so the timeline's 40px gap is
    // measured from the bubble's bottom edge rather than from below
    // the actions. Its 4px offset plus 32px height leave 4px of
    // clearance inside that gap, so it can never touch the next
    // message. Response actions stay in flow by contrast — that
    // asymmetry is in the design, not an oversight.
    userActions: {
      display: 'flex',
      alignItems: 'center',
      gap: theme.new.spacing.xsmall,
      opacity: 0,
      transition: theme.transitions.create('opacity'),
      position: 'absolute',
      top: '100%',
      right: 0,
      marginTop: theme.new.spacing.xsmall,
    },
    userActionsPersistent: {
      opacity: 1,
    },
    assistantActions: {
      display: 'flex',
      alignItems: 'center',
      gap: theme.new.spacing.xsmall,
    },
    visualizationSlot: {
      width: '100%',
    },
    visualizationImages: {
      display: 'flex',
      flexDirection: 'column',
      gap: theme.spacing(1.5),
      width: '100%',
      minInlineSize: 0,
      margin: 0,
      padding: 0,
      border: 0,
    },
    visualizationImageArtifact: {
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'flex-start',
      gap: theme.new.spacing.xsmall,
      width: '100%',
    },
    visualizationImage: {
      display: 'block',
      width: '100%',
      maxWidth: '100%',
      height: 'auto',
      aspectRatio: '16 / 9',
      objectFit: 'contain',
      borderRadius: theme.new.shape.borderRadius.medium,
    },
    visualizationThumbnail: {
      position: 'relative',
      display: 'block',
      width: '100%',
      padding: 0,
      border: 'none',
      background: 'transparent',
      cursor: 'pointer',
      borderRadius: theme.new.shape.borderRadius.medium,
      overflow: 'hidden',
      '&:focus-visible': {
        outline: `2px solid ${theme.new.color.brand[600]}`,
        outlineOffset: '2px',
      },
      [`&:hover .${VISUALIZATION_OVERLAY_CLASS}, &:focus-visible .${VISUALIZATION_OVERLAY_CLASS}`]:
        {
          opacity: 1,
        },
    },
    visualizationOverlay: {
      position: 'absolute',
      inset: 0,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      // Figma hover treatment: brand-100 (#F0F6FF) overlay at 60%.
      backgroundColor: alpha(String(theme.new.color.brand[100]), 0.6),
      color: theme.new.color.textPrimary,
      opacity: 0,
      transition: theme.transitions.create('opacity'),
      pointerEvents: 'none',
    },
    citations: {
      width: '100%',
      minInlineSize: 0,
      margin: 0,
      padding: 0,
      border: 0,
      ...theme.typography.small_body_regular,
      lineHeight: 1.6,
      color: theme.new.color.textPrimary,
      textAlign: 'start',
      overflowWrap: 'anywhere',
      wordBreak: 'break-word',
    },
    citationItem: {
      ...theme.typography.small_body_regular,
      color: theme.new.color.textPrimary,
    },
    citationLink: {
      ...theme.typography.small_body_regular,
      color: theme.new.color.brand[600],
      textDecoration: 'underline',
      '&:hover': {
        textDecoration: 'none',
      },
    },
  };
});

export default useStyles;
